import { useCallback, useRef, useState } from 'react';

/**
 * Voice input hook shared with onboarding chat composer behavior.
 */
export default function useChatVoiceInput({ fallbackTranscript = '' } = {}) {
  const [listening, setListening] = useState(false);
  const [draft, setDraft] = useState('');
  const recognitionRef = useRef(null);
  const voiceTimerRef = useRef(null);

  const stopVoice = useCallback(() => {
    setListening(false);
    if (voiceTimerRef.current) {
      window.clearTimeout(voiceTimerRef.current);
      voiceTimerRef.current = null;
    }
    try {
      recognitionRef.current?.stop?.();
    } catch {
      // ignore
    }
  }, []);

  const handleToggleVoice = useCallback(() => {
    if (listening) {
      stopVoice();
      return;
    }

    const SpeechRecognition =
      typeof window !== 'undefined'
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;

    if (SpeechRecognition) {
      try {
        const recognition = new SpeechRecognition();
        recognition.lang = 'en-US';
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
        recognition.onresult = (event) => {
          const transcript = event.results?.[0]?.[0]?.transcript || '';
          if (transcript) setDraft(transcript);
          setListening(false);
        };
        recognition.onerror = () => {
          if (fallbackTranscript) setDraft(fallbackTranscript);
          setListening(false);
        };
        recognition.onend = () => {
          setListening(false);
        };
        recognitionRef.current = recognition;
        setListening(true);
        recognition.start();
        return;
      } catch {
        // Fall through to simulation.
      }
    }

    setListening(true);
    voiceTimerRef.current = window.setTimeout(() => {
      if (fallbackTranscript) setDraft(fallbackTranscript);
      setListening(false);
    }, 1400);
  }, [fallbackTranscript, listening, stopVoice]);

  return {
    draft,
    setDraft,
    listening,
    handleToggleVoice,
    stopVoice,
  };
}
